<script setup lang="ts">
import { reactive, ref } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useViewStore } from '@/stores/viewStore'
import { Info } from 'lucide-vue-next'

type ProfileUser = {
  id?: number
  firstname?: string
  lastname?: string
  email?: string
  assignment_scope_mask?: number | null
}

const props = withDefaults(defineProps<{
  initialUser: ProfileUser
  impersonating: boolean
  embedded?: boolean
}>(), {
  embedded: false,
})

const emit = defineEmits<{
  (e: 'profile-saved', user: ProfileUser): void
}>()

const viewStore = useViewStore()

const profile = reactive({
  firstname: props.initialUser.firstname || '',
  lastname: props.initialUser.lastname || '',
  email: props.initialUser.email || '',
})

const assignmentAreas = [
  { key: 'dashboard', label: 'Dashboard', bit: 1 },
  { key: 'rechtsgrundlagen', label: 'Rechtsgrundlagen', bit: 2 },
  { key: 'pflichten', label: 'Pflichten', bit: 4 },
  { key: 'tasks', label: 'Aufgaben', bit: 8 },
]

const passwords = reactive({
  current_password: '',
  password: '',
  password_confirmation: '',
})

const profileLoading = ref(false)
const passwordLoading = ref(false)
const assignmentLoadingBit = ref<number | null>(null)
const assignmentMask = ref(Number.isInteger(props.initialUser.assignment_scope_mask) ? Number(props.initialUser.assignment_scope_mask) : 0)
const profileMessage = ref('')
const profileError = ref('')
const assignmentMessage = ref('')
const assignmentError = ref('')
const passwordMessage = ref('')
const passwordError = ref('')

function csrfHeaders() {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  return {
    'X-CSRF-Token': token,
    Accept: 'application/json',
  }
}

async function saveProfile() {
  profileLoading.value = true
  profileMessage.value = ''
  profileError.value = ''

  try {
    const response = await axios.patch('/profile.json', {
      user: {
        firstname: profile.firstname,
        lastname: profile.lastname,
      },
    }, { headers: csrfHeaders() })

    const saved = response.data?.user || {}
    profile.firstname = saved.firstname || ''
    profile.lastname = saved.lastname || ''
    profile.email = saved.email || profile.email
    profileMessage.value = response.data?.message || 'Profil gespeichert.'
    emit('profile-saved', saved)
  } catch (err: any) {
    profileError.value = err.response?.data?.error || 'Profil konnte nicht gespeichert werden.'
  } finally {
    profileLoading.value = false
  }
}

function isAssignmentOwn(bit: number) {
  return (assignmentMask.value & bit) === bit
}

async function setAssignmentScope(bit: number, ownOnly: boolean) {
  const previousMask = assignmentMask.value
  const nextMask = ownOnly ? (previousMask | bit) : (previousMask & ~bit)

  assignmentMask.value = nextMask
  assignmentLoadingBit.value = bit
  assignmentMessage.value = ''
  assignmentError.value = ''

  try {
    const response = await axios.patch('/profile/assignment_scope.json', {
      user: {
        assignment_scope_mask: nextMask,
      },
    }, { headers: csrfHeaders() })

    const saved = response.data?.user || {}
    assignmentMask.value = Number(saved.assignment_scope_mask ?? nextMask)
    viewStore.setAssignmentScopeMask(assignmentMask.value)
    assignmentMessage.value = response.data?.message || 'Zuweisung gespeichert.'
    emit('profile-saved', saved)
  } catch (err: any) {
    assignmentMask.value = previousMask
    assignmentError.value = err.response?.data?.error || 'Zuweisung konnte nicht gespeichert werden.'
  } finally {
    assignmentLoadingBit.value = null
  }
}

async function changePassword() {
  passwordMessage.value = ''
  passwordError.value = ''

  if (passwords.password.length < 8) {
    passwordError.value = 'Das Kennwort muss mindestens 8 Zeichen lang sein.'
    return
  }

  if (passwords.password !== passwords.password_confirmation) {
    passwordError.value = 'Die Kennwörter stimmen nicht überein.'
    return
  }

  passwordLoading.value = true

  try {
    const response = await axios.patch('/profile/password.json', { user: { ...passwords } }, { headers: csrfHeaders() })
    passwords.current_password = ''
    passwords.password = ''
    passwords.password_confirmation = ''
    passwordMessage.value = response.data?.message || 'Kennwort geändert.'
  } catch (err: any) {
    passwordError.value = err.response?.data?.error || 'Kennwort konnte nicht geändert werden.'
  } finally {
    passwordLoading.value = false
  }
}
</script>

<template>
  <div :class="embedded ? 'bg-white' : 'min-h-screen bg-white px-4 py-10'">
    <div v-if="!embedded" class="mx-auto w-full max-w-2xl">
      <a href="/" class="mb-4 inline-flex text-sm underline">Zurück</a>
    </div>
    <Card :class="embedded ? 'border-0 shadow-none' : 'mx-auto w-full max-w-2xl'">
      <CardHeader v-if="!embedded">
        <CardTitle>Profil</CardTitle>
      </CardHeader>
      <CardContent :class="embedded ? 'space-y-8 p-0' : 'space-y-8'">
        <form class="space-y-5" @submit.prevent="saveProfile">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2">
              <Label for="profile_firstname">Vorname</Label>
              <Input id="profile_firstname" v-model="profile.firstname" />
            </div>
            <div class="space-y-2">
              <Label for="profile_lastname">Nachname</Label>
              <Input id="profile_lastname" v-model="profile.lastname" />
            </div>
            <div class="md:col-span-2 space-y-2">
              <Label for="profile_email">E-Mail</Label>
              <Input id="profile_email" v-model="profile.email" type="email" disabled class="bg-muted" />
            </div>
          </div>

          <p v-if="profileMessage" class="text-sm text-green-700">{{ profileMessage }}</p>
          <p v-if="profileError" class="text-sm text-red-700">{{ profileError }}</p>

          <div class="flex flex-wrap items-center gap-3">
            <Button :disabled="profileLoading" class="greenbutton text-white">
              {{ profileLoading ? 'Speichern...' : 'Profil speichern' }}
            </Button>
          </div>
        </form>

        <div class="border-t pt-8">
          <div class="mb-4 flex items-center gap-2">
            <h2 class="text-lg font-semibold">Standard-Zuweisung</h2>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  class="h-7 w-7 text-gray-500 hover:text-gray-900"
                  aria-label="Information zur Standard-Zuweisung"
                >
                  <Info class="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent class="z-[10010] max-w-sm whitespace-normal text-sm leading-relaxed">
                Legen Sie fest, mit welcher Filteransicht Sie in den jeweiligen Bereich starten – „Eigene" für nur Ihnen bzw. Ihrer Funktion zugewiesene Datensätze, oder „Alle" für den vollständigen Datenbestand. Der Filter lässt sich innerhalb des Bereichs jederzeit anpassen; hier legen Sie nur den Ausgangszustand pro Sitzung fest.
              </TooltipContent>
            </Tooltip>
          </div>
          <div class="space-y-4">
            <div
              v-for="area in assignmentAreas"
              :key="area.key"
              class="flex items-center justify-between gap-4 rounded-md border p-3"
            >
              <div>
                <div class="text-sm font-medium text-gray-900">{{ area.label }}</div>
                <div class="text-xs text-gray-500">
                  {{ isAssignmentOwn(area.bit) ? 'Eigene' : 'Alle' }}
                </div>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-xs text-gray-500">Alle</span>
                <Switch
                  :id="`assignment_scope_${area.key}`"
                  :model-value="isAssignmentOwn(area.bit)"
                  :disabled="assignmentLoadingBit !== null"
                  @update:model-value="value => setAssignmentScope(area.bit, Boolean(value))"
                />
                <span class="text-xs text-gray-500">Eigene</span>
              </div>
            </div>
          </div>

          <p v-if="assignmentMessage" class="mt-3 text-sm text-green-700">{{ assignmentMessage }}</p>
          <p v-if="assignmentError" class="mt-3 text-sm text-red-700">{{ assignmentError }}</p>
        </div>

        <div class="border-t pt-8">
          <h2 class="text-lg font-semibold mb-4">Kennwort ändern</h2>

          <p v-if="impersonating" class="text-sm text-red-700">
            Während einer Nutzer-Ansicht kann das Kennwort nicht geändert werden.
          </p>

          <form v-else class="space-y-4" @submit.prevent="changePassword">
            <div class="space-y-2">
              <Label for="current_password">Aktuelles Kennwort</Label>
              <Input id="current_password" v-model="passwords.current_password" type="password" required autocomplete="current-password" />
            </div>
            <div class="space-y-2">
              <Label for="profile_new_password">Neues Kennwort</Label>
              <Input id="profile_new_password" v-model="passwords.password" type="password" required minlength="8" autocomplete="new-password" />
            </div>
            <div class="space-y-2">
              <Label for="profile_new_password_confirmation">Kennwort bestätigen</Label>
              <Input id="profile_new_password_confirmation" v-model="passwords.password_confirmation" type="password" required minlength="8" autocomplete="new-password" />
            </div>

            <p v-if="passwordMessage" class="text-sm text-green-700">{{ passwordMessage }}</p>
            <p v-if="passwordError" class="text-sm text-red-700">{{ passwordError }}</p>

            <Button :disabled="passwordLoading" class="greenbutton text-white">
              {{ passwordLoading ? 'Speichern...' : 'Kennwort ändern' }}
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  </div>
</template>
